import * as VideoProcessors from '@twilio/video-processors';
import {BackgroundProcessor} from '@twilio/video-processors/es5/processors/background/BackgroundProcessor';
import {forEach, isEqual} from 'lodash';
import {PureComponent, createRef} from 'react';
import {withTranslation, WithTranslation} from 'react-i18next';
import Video,
{LocalAudioTrack, LocalAudioTrackPublication, LocalVideoTrack, LocalVideoTrackPublication, Room, Participant} from 'twilio-video';

import {Loader} from 'components';
import {LOCAL_MEDIA_ID, SCREEN_SHARING_VIDEO_TRACK, VIDEO_PIN_AUTO_HIDE_TIMEOUT, DATA_PARTICIPANT_ID} from 'constants/session';
import {VirtualBackgroundType} from 'enums';
import {MediaDevicesConstraints, VirtualBackgroundConfig} from 'types';
import './styles.scss';
import {logger, sessionUtils} from 'utils';

type State = {
    readonly trackSubscribeChangeDate: Date;
    readonly backgroundProcessor: BackgroundProcessor;
    readonly activeOverlayParticipant: string | null;
    readonly overlayTimeout: ReturnType<typeof setTimeout> | null;
}

const initialState: State = {
    trackSubscribeChangeDate: null,
    backgroundProcessor: null,
    activeOverlayParticipant: null,
    overlayTimeout: null,
};

type Props = {
    room: Room;
    participantIds?: string[];
    mediaConstraints?: MediaDevicesConstraints;
    isAudioEnabled?: boolean;
    isVideoEnabled?: boolean;
    virtualBackgroundConfig?: VirtualBackgroundConfig;
    pinnedParticipantId?: string;
    asColumn?: boolean;
    onLocalNetworkQualityLevelChange?: (level: number) => void;
    onRemoteNetworkQualityLevelChange?: (level: number) => void;
    renderOverParticipantContent?: (participantId: string) => React.ReactNode;
} & WithTranslation;

class TwilioVideoConference extends PureComponent<Props, State> {

    readonly state = initialState;
    static readonly defaultProps = {
        isAudioEnabled: true,
        isVideoEnabled: true,
        asColumn: false,
    };

    localMediaRef = createRef<HTMLDivElement>();
    remoteVideosRef = createRef<HTMLDivElement>();
    containerRef = createRef<HTMLDivElement>();

    _isMounted = false;
    _resizeObserver: ResizeObserver | null = null;

    componentDidMount() {
        this._isMounted = true;

        if (this.containerRef.current?.parentElement) {
            this._resizeObserver = new ResizeObserver(() => {
                this.updateDimensionsAndApplyStyles();
            });
            this._resizeObserver.observe(this.containerRef.current.parentElement);
        }

        const {localMediaRef} = this;
        const localMediaEl = localMediaRef.current as HTMLElement;

        if (!localMediaEl) {
            return;
        }

        if (!localMediaEl.querySelector('video')) {
            this.participantConnected(this.props.room.localParticipant, true);
        }

        this.subscribeRoomEvents();
        this.toggleAudio();
        this.toggleVideo();

        this.updateDimensionsAndApplyStyles();
    }

    componentDidUpdate(prevProps: Props) {
        if (this.props.isAudioEnabled !== prevProps.isAudioEnabled) {
            this.toggleAudio();
        }
        if (this.props.isVideoEnabled !== prevProps.isVideoEnabled) {
            this.toggleVideo();
        }
        if (this.props.mediaConstraints) {
            if (!isEqual(this.props.mediaConstraints?.videoinput, prevProps.mediaConstraints?.videoinput)) {
                this.updateVideoDevice(this.props.mediaConstraints?.videoinput);
            }
            if (prevProps.mediaConstraints?.audiooutput !== this.props.mediaConstraints?.audiooutput) {
                this.updateAudioOutputDevice(this.props.mediaConstraints.audiooutput);
            }
            if (!isEqual(prevProps.mediaConstraints?.audioinput, this.props.mediaConstraints?.audioinput)) {
                this.updateAudioInputDevice(this.props.mediaConstraints.audioinput as MediaTrackConstraintSet);
            }
        }
        if (this.props.virtualBackgroundConfig !== prevProps.virtualBackgroundConfig) {
            this.setVirtualBackground();
        }
        if (this.props.pinnedParticipantId !== prevProps.pinnedParticipantId) {
            this.updateDimensionsAndApplyStyles();
        }
        if (this.props.asColumn !== prevProps.asColumn) {
            this.updateDimensionsAndApplyStyles();
        }
    }

    componentWillUnmount() {
        this.cleanupParticipantTracks(this.props.room.localParticipant);
        if (this._resizeObserver) {
            this._resizeObserver.disconnect();
            this._resizeObserver = null;
        }
        if (this.state.overlayTimeout) {
            clearTimeout(this.state.overlayTimeout);
        }
        this._isMounted = false;
    }

    handleParticipantTrack = (publication, isLocal: boolean, participantId: string) => {
        let container: HTMLElement;

        if (isLocal) {
            container = this.localMediaRef.current;
        } else {
            container = this.containerRef.current?.querySelector(sessionUtils.getParticipantVideoSelector(participantId));
        }

        if (container) {
            this.publishTrack(publication, container);
            if (!isLocal) {
                const audioElements = container?.getElementsByTagName('audio');
                forEach(Array.from(audioElements), (audioEl: any) => audioEl?.setSinkId?.(this.props.mediaConstraints?.audiooutput));
            }
        } else if (!isLocal) {
            setTimeout(() => {
                this.handleParticipantTrack(publication, isLocal, participantId);
            }, 100);
        }
    };

    participantConnected = (participant: Participant, isLocal: boolean) => {
        if (!isLocal) {
            this.showParticipant(participant.identity);
            this.updateDimensionsAndApplyStyles();
        }

        participant.on('trackPublished', (publication) => {
            this.handleParticipantTrack(publication, isLocal, participant.identity);
        });

        participant.on('trackUnpublished', (publication) => {
            if (publication.trackName === SCREEN_SHARING_VIDEO_TRACK) {
                return;
            }
            if (this._isMounted) {
                // eslint-disable-next-line react/no-unused-state
                this.setState({trackSubscribeChangeDate: new Date()});
            }
            if (publication.track) {
                this.detachTrack(publication.track);
            } else if (!publication.isSubscribed) {
                let videoContainer: HTMLElement;
                if (isLocal) {
                    videoContainer = this.localMediaRef.current;
                } else {
                    videoContainer = this.containerRef.current?.querySelector(
                        sessionUtils.getParticipantVideoSelector(participant.identity)
                    );
                }
                videoContainer?.querySelector(publication.kind)?.remove();
            }
        });

        if (this._isMounted) {
            if (isLocal) {
                this.props.onLocalNetworkQualityLevelChange?.(participant.networkQualityLevel);
            } else {
                this.props.onRemoteNetworkQualityLevelChange?.(participant.networkQualityLevel);
            }
        }

        participant.on('trackPublicationFailed', (error, localTrack) => {
            logger.warn({category: logger.category.Twilio, message: `Failed to publish LocalTrack ${localTrack.name}: ${error.message}`});
        });

        participant.on('networkQualityLevelChanged', (level) => {
            if (this._isMounted) {
                if (isLocal) {
                    this.props.onLocalNetworkQualityLevelChange?.(level);
                } else {
                    this.props.onRemoteNetworkQualityLevelChange?.(level);
                }
            }
        });

        participant.tracks.forEach((publication) => {
            this.handleParticipantTrack(publication, isLocal, participant.identity);
        });
    };

    publishTrack = (publication, container) => {
        if (publication.trackName === SCREEN_SHARING_VIDEO_TRACK) {
            return;
        }
        if (this._isMounted) {
            // eslint-disable-next-line react/no-unused-state
            this.setState({trackSubscribeChangeDate: new Date()});
        }
        if (publication.track) {
            this.attachTrack(container, publication.track);
        } else if (!publication.isSubscribed) {
            publication.on('subscribed', (track) => {
                this.attachTrack(container, track);
            });
            publication.on('unsubscribed', this.detachTrack);
        }
    };

    attachTrack = (container: HTMLElement, track: LocalVideoTrack | LocalAudioTrack) => {
        if (!container || !track) {
            return;
        }

        const existingElement = container.querySelector(track.kind);
        if (existingElement) {
            existingElement.remove();
        }

        const trackElement = track.attach();
        if (track.kind === 'video') {
            (trackElement as HTMLVideoElement).style.width = '100%';
            (trackElement as HTMLVideoElement).style.height = '100%';
            (trackElement as HTMLVideoElement).style.objectFit = 'cover';
        }

        container.appendChild(trackElement);

        const loader = container.querySelector('.loader');
        if (loader && track.kind === 'video') {
            loader.remove();
        }

        // eslint-disable-next-line react/no-unused-state
        track.on('enabled', () => this.setState({trackSubscribeChangeDate: new Date()}));
        // eslint-disable-next-line react/no-unused-state
        track.on('disabled', () => this.setState({trackSubscribeChangeDate: new Date()}));
    };

    detachTrack = (track) => {
        track.mediaStreamTrack.enabled = false;
        track.detach().forEach((element) => {
            element.remove();
            element.srcObject = null;
        });
    };

    subscribeRoomEvents = () => {
        const {room} = this.props;

        room.participants.forEach((participant) => {
            this.participantConnected(participant, false);
        });

        room.on('trackSubscriptionFailed', (error, remoteTrackPublication, remoteParticipant) => {
            logger.warn({
                category: logger.category.Twilio,
                message: `Failed to subscribe to RemoteTrack ${remoteTrackPublication.trackName}` +
                ` from RemoteParticipant ${remoteParticipant.identity}: ${error.message}`
            });
        });

        room.on('participantConnected', (participant) => {
            this.participantConnected(participant, false);
        });

        room.on('participantDisconnected', (participant) => {
            this.cleanupParticipantTracks(participant);
            this.hideParticipant(participant.identity);
            this.updateDimensionsAndApplyStyles();
        });

        room.on('disconnected', () => {
            this.cleanupParticipantTracks(room.localParticipant);
            room.participants.forEach(this.cleanupParticipantTracks);
        });
    };

    cleanupParticipantTracks = (participant) => {
        const publicationTracks = Array.from(participant.tracks.values()) as any[];

        publicationTracks.forEach((publication) => {
            if (publication.trackName === SCREEN_SHARING_VIDEO_TRACK) {
                return;
            }
            if (publication.track) {
                publication.track.stop();
                participant.unpublishTrack(publication.track);
                this.detachTrack(publication.track);
            }
        });
    };

    updateMeetLayout = (count: number) => {
        const container = this.containerRef.current?.querySelector('.participants-grid') as HTMLElement;
        if (!container || !count) {
            return;
        }

        const pinnedParticipantId = this.props.pinnedParticipantId;
        const participants = this.getVisibleParticipants();
        const tiles = participants.map((participantId) =>
            container.querySelector(sessionUtils.getParticipantVideoSelector(participantId)) as HTMLElement
        ).filter(Boolean);

        if (!tiles.length) {
            return;
        }

        if (this.props.asColumn) {
            this.updateColumnLayout(container, tiles, tiles.length);
        } else if (pinnedParticipantId && tiles.length > 1) {
            const pinnedIndex = participants.findIndex((id) => id === pinnedParticipantId);
            if (pinnedIndex !== -1) {
                this.updatePinnedLayout(container, tiles, pinnedIndex);
            } else {
                this.updateGridLayout(container, tiles, tiles.length);
            }
        } else {
            this.updateGridLayout(container, tiles, tiles.length);
        }
    };

    updateColumnLayout = (container: HTMLElement, tiles: HTMLElement[], count: number) => {
        const AR = 4 / 3;
        const parentW = container.parentElement?.clientWidth || container.clientWidth;
        const containerW = parentW * 0.2;
        const H = container.clientHeight;

        container.style.display = 'flex';
        container.style.flexDirection = 'column';
        container.style.alignItems = 'center';
        container.style.width = `${containerW}px`;
        container.style.position = 'absolute';
        container.style.right = '16px';
        container.style.top = '16px';
        container.style.bottom = '16px';
        container.style.height = 'calc(100% - 32px)';
        container.style.gridTemplateColumns = '';
        container.style.gridTemplateRows = '';

        const gap = 16;
        const availableW = containerW - 32;
        const availableH = H - (count - 1) * gap;
        const tileH = availableH / count;
        const tileWByHeight = tileH * AR;
        const tileW = Math.min(availableW, tileWByHeight);

        tiles.forEach((tile) => {
            tile.style.width = `${tileW}px`;
            tile.style.height = `${tileW / AR}px`;
            tile.style.position = 'relative';
            tile.style.left = '';
            tile.style.top = '';
            tile.style.bottom = '';
            tile.style.margin = `${gap / 2}px 0`;
            tile.style.justifySelf = '';
            tile.style.alignSelf = '';
            tile.style.gridColumn = '';
            tile.style.gridRow = '';
            tile.classList.remove('pinned-participant');
        });

        if (tiles.length > 0) {
            tiles[0].style.marginTop = '0';
            tiles[tiles.length - 1].style.marginBottom = '0';
        }
    };

    updatePinnedLayout = (container: HTMLElement, tiles: HTMLElement[], pinnedIndex: number) => {
        const AR = 4 / 3;
        const W = container.clientWidth;
        const H = container.clientHeight;
        const n = tiles.length;

        container.style.display = 'block';
        container.style.gridTemplateColumns = '';
        container.style.gridTemplateRows = '';
        container.style.position = '';
        container.style.right = '';
        container.style.top = '';
        container.style.bottom = '';
        container.style.width = '';
        container.style.height = '';
        container.style.alignItems = '';
        container.style.flexDirection = '';

        const gap = 16;
        const mainAreaW = W * 0.75 - gap / 2;
        const mainAreaH = H;

        const mainW = Math.min(mainAreaW, mainAreaH * AR);
        const mainH = mainW / AR;

        const mainLeft = (mainAreaW - mainW) / 2;
        const mainTop = (H - mainH) / 2;

        const sideAreaW = W * 0.25 - gap / 2;
        const sideAreaX = W * 0.75 + gap / 2;
        const sideCount = n - 1;

        let sideTileW = sideAreaW;
        let sideTileH = sideTileW / AR;

        if (sideCount > 0) {
            const totalH = sideCount * sideTileH + (sideCount - 1) * gap;
            if (totalH > H) {
                const scale = H / totalH;
                sideTileW *= scale;
                sideTileH *= scale;
            }
        }

        const sidebarInnerX = sideAreaX + (sideAreaW - sideTileW) / 2;
        const adjustedSideTileH = sideCount > 1 ? sideTileH : sideTileH;
        const totalSidebarH = sideCount * adjustedSideTileH + (sideCount - 1) * gap;
        const startY = (H - totalSidebarH) / 2;

        tiles.forEach((tile, i) => {
            tile.style.position = 'absolute';
            tile.style.justifySelf = '';
            tile.style.alignSelf = '';
            tile.style.gridColumn = '';
            tile.style.gridRow = '';
            tile.style.margin = '';

            if (i === pinnedIndex) {
                tile.style.left = `${mainLeft}px`;
                tile.style.top = `${mainTop}px`;
                tile.style.width = `${mainW}px`;
                tile.style.height = `${mainH}px`;
                tile.classList.add('pinned-participant');
            } else {
                const logicalIndex = i < pinnedIndex ? i : i - 1;
                const top = startY + logicalIndex * (adjustedSideTileH + gap);

                tile.style.left = `${sidebarInnerX}px`;
                tile.style.top = `${top}px`;
                tile.style.width = `${sideTileW}px`;
                tile.style.height = `${adjustedSideTileH}px`;
                tile.classList.remove('pinned-participant');
            }
        });
    };

    updateGridLayout = (container: HTMLElement, tiles: HTMLElement[], count: number) => {

        const AR = 4 / 3;

        container.style.display = 'grid';
        container.style.position = '';
        container.style.right = '';
        container.style.top = '';
        container.style.bottom = '';
        container.style.width = '';
        container.style.height = '';
        container.style.alignItems = '';
        container.style.flexDirection = '';

        // Wait for layout recalculation
        requestAnimationFrame(() => {
            if (!this._isMounted || !container) {
                return;
            }

            // Get dimensions after resetting styles
            const W = container.clientWidth;
            const H = container.clientHeight;

            this.continueGridLayout(container, tiles, count, AR, W, H);
        });
    };

    continueGridLayout = (container: HTMLElement, tiles: HTMLElement[], count: number, AR: number, W: number, H: number) => {
        // Account for grid margin and gaps
        const gridMargin = 16; // 8px on each side
        const gridGap = 12;
        const actualW = W - gridMargin;
        const actualH = H - gridMargin;

        // Reset all tile styles first
        tiles.forEach((tile) => {
            tile.style.position = '';
            tile.style.left = '';
            tile.style.top = '';
            tile.style.bottom = '';
            tile.style.margin = '';
            tile.style.justifySelf = '';
            tile.style.alignSelf = '';
            tile.style.gridColumn = '';
            tile.style.gridRow = '';
        });

        let best = null;

        for (let cols = 1; cols <= count; cols++) {
            const rows = Math.ceil(count / cols);
            const maxW = (actualW - (cols - 1) * gridGap) / cols;
            const maxH = (actualH - (rows - 1) * gridGap) / rows;

            const tileW = Math.min(maxW, maxH * AR);
            const tileH = tileW / AR;

            if (tileW <= 0 || tileH <= 0) {
                continue;
            }

            const area = tileW * tileH;
            if (!best || area > best.area) {
                best = {cols, rows, tileW, tileH, area};
            }
        }

        if (count === 3) {
            const cols = 2;
            const rows = 2;
            const maxW = (actualW - (cols - 1) * gridGap) / cols;
            const maxH = (actualH - (rows - 1) * gridGap) / rows;

            const tileW = Math.min(maxW, maxH * AR);
            const tileH = tileW / AR;

            best = {cols, rows, tileW, tileH, area: tileW * tileH};
        }

        if (!best) {
            return;
        }

        const {cols, rows, tileW, tileH} = best;

        container.style.gridTemplateColumns = `repeat(${cols}, ${tileW}px)`;
        container.style.gridTemplateRows = `repeat(${rows}, ${tileH}px)`;

        tiles.forEach((tile) => {
            tile.style.width = `${tileW}px`;
            tile.style.height = `${tileH}px`;
            tile.style.justifySelf = 'center';
            tile.style.alignSelf = 'center';
            tile.style.gridColumn = '';
            tile.style.gridRow = '';
            tile.style.position = 'relative';
            tile.style.left = '';
            tile.style.top = '';
            tile.style.bottom = '';
            tile.style.margin = '';
            tile.classList.remove('pinned-participant');
        });

        if (count === 3 && tiles[2]) {
            tiles[2].style.gridColumn = '1 / span 2';
            tiles[2].style.justifySelf = 'center';
        }
    };

    renderParticipants = () => {
        const {participantIds} = this.props;
        const allParticipantIds = participantIds || [this.props.room.localParticipant.identity];
        const connectedParticipants = [this.props.room.localParticipant, ...Array.from(this.props.room.participants.values())];
        const connectedParticipantIds = connectedParticipants.map((p) => p.identity);

        const renderParticipantDiv = (participantId: string, index: number) => {
            const isLocal = participantId === this.props.room.localParticipant.identity;
            const isVisible = connectedParticipantIds.includes(participantId);
            const overlayVisible = this.state.activeOverlayParticipant === participantId;

            return (
                <div
                    key={`participant-${participantId}`}
                    ref={isLocal ? this.localMediaRef : null}
                    data-participant-id={participantId}
                    data-slot-index={index}
                    className={`participant-container ${!isVisible ? 'tw-hidden' : ''}`}
                    id={isLocal ? LOCAL_MEDIA_ID : `participant-${participantId}`}
                    onMouseMove={() => this.handleMouseMoveParticipant(participantId)}
                    onMouseOut={() => this.handleMouseOutParticipant()}
                >
                    <Loader
                        className='loader tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center'
                        svgClassName='tw-max-w-12 tw-max-h-12 screenshot-hidden'
                    />
                    {overlayVisible && this.props.renderOverParticipantContent && (
                        <div style={{position: 'absolute', top: 0, left: 0, right: 0, bottom: 0}}>
                            {this.props.renderOverParticipantContent(participantId)}
                        </div>
                    )}
                </div>
            );
        };

        return (
            <div className='participants-grid'>
                {allParticipantIds.map((participantId, index) => renderParticipantDiv(participantId, index))}
            </div>
        );
    };

    updateAudioInputDevice = (props: Video.CreateLocalTrackOptions) => {
        this.props.room.localParticipant.audioTracks.forEach((publication) => {
            publication.track.restart(props).catch((error) => {
                logger.warn({
                    category: logger.category.Twilio,
                    message: `Failed to restart audio track with device ${props?.deviceId}: ${error.message}`
                });
            });
        });
    };

    updateAudioOutputDevice = (newDeviceId: string) => {
        const containers = this.containerRef.current?.querySelectorAll(`[${DATA_PARTICIPANT_ID}]`);
        containers?.forEach((container) => {
            const audioElements = container.getElementsByTagName('audio');
            forEach(Array.from(audioElements), (audioEl: any) => audioEl?.setSinkId?.(newDeviceId));
        });
    };

    updateVideoDevice = (props: Video.CreateLocalTrackOptions) => {
        this.props.room.localParticipant.videoTracks.forEach((publication) => {
            if (publication.trackName !== SCREEN_SHARING_VIDEO_TRACK) {
                publication.track.restart(props).catch((error) => {
                    logger.warn({
                        category: logger.category.Twilio,
                        message: `Failed to restart video track with device ${props?.deviceId}: ${error.message}`
                    });
                });
            }
        });
    };

    toggleAudio = () => {
        const {isAudioEnabled, room} = this.props;
        this.toggleLocalMedia(isAudioEnabled, room.localParticipant.audioTracks);
    };

    toggleVideo = () => {
        const {isVideoEnabled, room, virtualBackgroundConfig} = this.props;
        this.toggleLocalMedia(isVideoEnabled, room.localParticipant.videoTracks);

        if (isVideoEnabled && virtualBackgroundConfig) {
            this.setVirtualBackground();
        } else if (!isVideoEnabled) {
            this.removeVirtualBackground();
        }
    };

    toggleLocalMedia = (isEnabled: boolean, tracks) => {
        const mediaTracksPublications: (LocalVideoTrackPublication | LocalAudioTrackPublication)[] =
            Array.from(tracks.values());

        mediaTracksPublications
            .map((publicationTrack) => publicationTrack.track)
            .filter((track) => track.name !== SCREEN_SHARING_VIDEO_TRACK)
            .forEach((track) => {
                if (isEnabled) {
                    track.enable();
                } else {
                    track.disable();
                }
            });
    };

    getVirtualBackgroundProcessor() {
        return new Promise<BackgroundProcessor>((resolve) => {
            const {type, image} = this.props.virtualBackgroundConfig;
            if (type === VirtualBackgroundType.Blur) {
                resolve(new VideoProcessors.GaussianBlurBackgroundProcessor({
                    assetsPath: '/assets/twilio',
                    maskBlurRadius: 10,
                    blurFilterRadius: 5,
                }));
            } else if (type === VirtualBackgroundType.Image && image) {
                const img = new Image();
                img.crossOrigin = 'anonymous';
                img.src = image;
                img.onload = () => {
                    resolve(new VideoProcessors.VirtualBackgroundProcessor({
                        assetsPath: '/assets/twilio',
                        backgroundImage: img,
                        maskBlurRadius: 5,
                        fitType: VideoProcessors.ImageFit.Fill
                    }));
                };
                img.onerror = () => {
                    logger.warn({
                        category: logger.category.Twilio, message: `Failed to load virtual background image: ${image}`
                    });
                    resolve(null);
                };
            } else if (type === VirtualBackgroundType.None && this.state.backgroundProcessor) {
                resolve(null);
            }
        });
    }

    removeVirtualBackground() {
        if (this.state.backgroundProcessor) {
            this.props.room.localParticipant.videoTracks.forEach((publication) => {
                if (publication.track.kind === 'video' && publication.trackName !== SCREEN_SHARING_VIDEO_TRACK) {
                    publication.track.removeProcessor(this.state.backgroundProcessor);
                }
            });
            this.setState({backgroundProcessor: null});
        }
    }

    async setVirtualBackground() {
        const processor = await this.getVirtualBackgroundProcessor();
        this.props.room.localParticipant.videoTracks.forEach(async (publication) => {
            if (publication.track.kind === 'video' && publication.trackName !== SCREEN_SHARING_VIDEO_TRACK) {
                if (this.state.backgroundProcessor) {
                    publication.track.removeProcessor(this.state.backgroundProcessor);
                }
                if (processor) {
                    await processor.loadModel();
                    publication.track.addProcessor(processor, {
                        inputFrameBufferType: 'video',
                        outputFrameBufferContextType: 'webgl2',
                    });
                }
            }
        });
        this.setState({backgroundProcessor: processor});
    }

    showParticipant = (participantId: string) => {
        const container = this.containerRef.current?.querySelector(sessionUtils.getParticipantVideoSelector(participantId)) as HTMLElement;
        if (container) {
            container.classList.remove('tw-hidden');
        }
    };

    hideParticipant = (participantId: string) => {
        const container = this.containerRef.current?.querySelector(sessionUtils.getParticipantVideoSelector(participantId)) as HTMLElement;
        if (container) {
            container.classList.add('tw-hidden');
        }
    };

    getVisibleParticipants = () => {
        const {participantIds} = this.props;
        const allParticipantIds = participantIds || [this.props.room.localParticipant.identity];
        const connectedParticipants = [this.props.room.localParticipant, ...Array.from(this.props.room.participants.values())];
        const connectedParticipantIds = connectedParticipants.map((p) => p.identity);

        return allParticipantIds.filter((id) => connectedParticipantIds.includes(id));
    };

    updateDimensionsAndApplyStyles = () => {
        if (!this.containerRef.current) {
            return;
        }

        const visibleParticipants = this.getVisibleParticipants();

        // Wait for next frame to ensure container styles are applied before calculating children
        requestAnimationFrame(() => {
            if (this._isMounted && this.containerRef.current) {
                this.updateMeetLayout(visibleParticipants.length);
            }
        });
    };

    handleMouseMoveParticipant = (participantId: string) => {
        if (this.state.overlayTimeout) {
            clearTimeout(this.state.overlayTimeout);
        }
        const timeout = setTimeout(() => {
            if (this._isMounted) {
                this.setState({
                    activeOverlayParticipant: null,
                    overlayTimeout: null
                });
            }
        }, VIDEO_PIN_AUTO_HIDE_TIMEOUT);
        this.setState({
            activeOverlayParticipant: participantId,
            overlayTimeout: timeout
        });
    };

    handleMouseOutParticipant = () => {
        if (this.state.overlayTimeout) {
            clearTimeout(this.state.overlayTimeout);
        }
        this.setState({
            activeOverlayParticipant: null,
            overlayTimeout: null
        });
    };

    render() {
        return (
            <div
                ref={this.containerRef}
                className='twilio-video-multi-container tw-w-full tw-h-full tw-max-h-full tw-overflow-hidden tw-p-4'
            >
                {this.renderParticipants()}
                <div ref={this.remoteVideosRef} className='tw-hidden' />
            </div>
        );
    }
}

export default withTranslation()(TwilioVideoConference);